type Confetti = typeof import('canvas-confetti')
type Shape = ReturnType<Confetti['shapeFromPath']>

type Origin = {
    x: number
    y: number
}

const COLORS = ['#e8436f', '#ff8fab', '#ffc2d1', '#f6c453', '#ffffff']
const HEART_PATH = 'M167 72c19,-38 37,-56 75,-56 42,0 76,33 76,75 0,76 -76,151 -151,227 -76,-76 -151,-151 -151,-227 0,-42 33,-75 75,-75 38,0 57,18 76,56z'

let loader: Promise<Confetti> | null = null
let heart: Shape | null = null

const load = (): Promise<Confetti> => {
    if (!loader) loader = import('canvas-confetti').then((imported) => imported.default)

    return loader
}

const prefersReducedMotion = (): boolean => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

const randomBetween = (min: number, max: number): number => Math.random() * (max - min) + min

// Origin of an element's centre in the 0 to 1 viewport units confetti expects.
export const originOf = (element: Element | null): Origin | undefined => {
    if (!element) return undefined

    const rect = element.getBoundingClientRect()

    return {
        x: (rect.left + rect.width / 2) / window.innerWidth,
        y: (rect.top + rect.height / 2) / window.innerHeight
    }
}

export const heartBurst = async (origin: Origin = { x: 0.5, y: 0.55 }): Promise<void> => {
    if (typeof window === 'undefined' || prefersReducedMotion()) return

    try {
        const confetti = await load()

        if (!heart) heart = confetti.shapeFromPath({ path: HEART_PATH })

        confetti({
            particleCount: 60,
            spread: 80,
            startVelocity: 38,
            gravity: 0.85,
            scalar: 1.4,
            ticks: 240,
            origin,
            colors: COLORS,
            shapes: [heart],
            zIndex: 70
        })

        confetti({
            particleCount: 70,
            spread: 110,
            startVelocity: 32,
            origin,
            colors: COLORS,
            zIndex: 70
        })
    }
    catch {
        // Confetti is decoration only, the page works without it.
    }
}

export const fireworks = async (duration = 5000): Promise<() => void> => {
    if (typeof window === 'undefined' || prefersReducedMotion()) return () => undefined

    try {
        const confetti = await load()
        const end = Date.now() + duration

        const timer = window.setInterval(() => {
            const left = end - Date.now()

            if (left <= 0) {
                window.clearInterval(timer)
                return
            }

            const particleCount = Math.round(48 * (left / duration))
            const shared = {
                particleCount,
                startVelocity: 30,
                spread: 360,
                ticks: 70,
                zIndex: 70,
                colors: COLORS
            }

            confetti({ ...shared, origin: { x: randomBetween(0.1, 0.3), y: randomBetween(0.1, 0.5) } })
            confetti({ ...shared, origin: { x: randomBetween(0.7, 0.9), y: randomBetween(0.1, 0.5) } })
        }, 250)

        return () => window.clearInterval(timer)
    }
    catch {
        return () => undefined
    }
}
